import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { sendVerificationCode, verifyCode } from '../api';
import './UserAuth.css';

function UserAuth() {
    const [email, setEmail] = useState('');
    const [name, setName] = useState('');
    const [code, setCode] = useState(['', '', '', '', '', '']);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [codeSent, setCodeSent] = useState(false);
    const [showRegisterModal, setShowRegisterModal] = useState(false);
    const [countdown, setCountdown] = useState(0);
    const [verifiedCode, setVerifiedCode] = useState('');
    const navigate = useNavigate();
    const codeRefs = useRef([]);
    const countdownRef = useRef(null);

    const startCountdown = () => {
        setCountdown(60);
        if (countdownRef.current) clearInterval(countdownRef.current);
        countdownRef.current = setInterval(() => {
            setCountdown(prev => {
                if (prev <= 1) {
                    clearInterval(countdownRef.current);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    };

    const handleSendCode = async (e) => {
        e.preventDefault();
        setLoading(true);
        setMessage('');
        setError('');

        try {
            await sendVerificationCode(email);
            setCodeSent(true);
            setMessage('Verification code sent to your email.');
            startCountdown();
            setTimeout(() => codeRefs.current[0]?.focus(), 100);
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to send verification code.');
        } finally {
            setLoading(false);
        }
    };

    const handleResendCode = async () => {
        if (countdown > 0) return;
        setLoading(true);
        setMessage('');
        setError('');

        try {
            await sendVerificationCode(email);
            setMessage('Verification code resent.');
            setCode(['', '', '', '', '', '']);
            startCountdown();
            codeRefs.current[0]?.focus();
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to resend code.');
        } finally {
            setLoading(false);
        }
    };

    const handleCodeChange = (index, value) => {
        if (!/^\d*$/.test(value)) return;

        const newCode = [...code];
        newCode[index] = value.slice(-1);
        setCode(newCode);

        if (value && index < 5) {
            codeRefs.current[index + 1]?.focus();
        }

        const fullCode = newCode.join('');
        if (fullCode.length === 6) {
            handleVerifyCode(fullCode);
        }
    };

    const handleCodeKeyDown = (index, e) => {
        if (e.key === 'Backspace' && !code[index] && index > 0) {
            codeRefs.current[index - 1]?.focus();
        }
    };

    const handleCodePaste = (e) => {
        e.preventDefault();
        const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
        if (pasted.length > 0) {
            const newCode = [...code];
            for (let i = 0; i < 6; i++) {
                newCode[i] = pasted[i] || '';
            }
            setCode(newCode);
            if (pasted.length === 6) {
                handleVerifyCode(pasted);
            } else {
                codeRefs.current[pasted.length]?.focus();
            }
        }
    };

    const handleVerifyCode = async (fullCode) => {
        setLoading(true);
        setMessage('');
        setError('');

        try {
            const result = await verifyCode(email, fullCode, false);

            if (result.is_new_user) {
                setVerifiedCode(fullCode);
                setShowRegisterModal(true);
            } else if (result.access_token) {
                localStorage.setItem('userToken', result.access_token);
                localStorage.setItem('userEmail', email);
                setMessage('Login successful! Redirecting...');
                setTimeout(() => navigate('/track'), 1000);
            }
        } catch (err) {
            setError(err.response?.data?.detail || 'Invalid verification code.');
            setCode(['', '', '', '', '', '']);
            codeRefs.current[0]?.focus();
        } finally {
            setLoading(false);
        }
    };

    const handleRegisterConfirm = async () => {
        setShowRegisterModal(false);
        setLoading(true);
        setMessage('');
        setError('');

        try {
            const result = await verifyCode(email, verifiedCode, true, name || null);

            if (result.access_token) {
                localStorage.setItem('userToken', result.access_token);
                localStorage.setItem('userEmail', email);
                setMessage('Registration successful! Redirecting...');
                setTimeout(() => navigate('/track'), 1000);
            }
        } catch (err) {
            setError(err.response?.data?.detail || 'Registration failed.');
            setCode(['', '', '', '', '', '']);
            codeRefs.current[0]?.focus();
        } finally {
            setLoading(false);
        }
    };

    const handleRegisterCancel = () => {
        setShowRegisterModal(false);
        setCode(['', '', '', '', '', '']);
        setError('');
        setMessage('');
        codeRefs.current[0]?.focus();
    };

    return (
        <div className="user-auth-container">
            <div className="auth-card">
                <h2>Login / Register</h2>
                <p className="auth-description">
                    Enter your email to receive a verification code
                </p>

                <form onSubmit={handleSendCode} className="auth-form">
                    <div className="form-group">
                        <label htmlFor="email">Email Address</label>
                        <input
                            type="email"
                            id="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="your@email.com"
                            required
                            disabled={codeSent}
                            maxLength={254}
                        />
                        {codeSent && (
                            <button
                                type="button"
                                className="link-button change-email-link"
                                onClick={() => { setCodeSent(false); setCode(['', '', '', '', '', '']); setMessage(''); setError(''); }}
                            >
                                Change email
                            </button>
                        )}
                    </div>

                    {codeSent && (
                        <div className="form-group">
                            <label>Verification Code</label>
                            <div className="code-inputs" onPaste={handleCodePaste}>
                                {code.map((digit, index) => (
                                    <input
                                        key={index}
                                        ref={el => codeRefs.current[index] = el}
                                        type="text"
                                        inputMode="numeric"
                                        maxLength={1}
                                        value={digit}
                                        onChange={(e) => handleCodeChange(index, e.target.value)}
                                        onKeyDown={(e) => handleCodeKeyDown(index, e)}
                                        className="code-input"
                                        autoComplete="one-time-code"
                                    />
                                ))}
                            </div>
                            <div className="resend-row">
                                <span>Didn't receive the code?</span>
                                <button
                                    type="button"
                                    className="link-button"
                                    onClick={handleResendCode}
                                    disabled={countdown > 0}
                                >
                                    {countdown > 0 ? `Resend in ${countdown}s` : 'Resend Code'}
                                </button>
                            </div>
                        </div>
                    )}

                    {message && <div className="success-message">{message}</div>}
                    {error && <div className="error-message">{error}</div>}
                    {loading && codeSent && <div className="loading-text">Verifying...</div>}

                    {!codeSent && (
                        <button type="submit" className="auth-button" disabled={loading}>
                            {loading ? 'Sending...' : 'Send Verification Code'}
                        </button>
                    )}
                </form>
            </div>

            {showRegisterModal && (
                <div className="modal-overlay">
                    <div className="modal-card">
                        <h3>Email Not Registered</h3>
                        <p>The email <strong>{email}</strong> is not registered yet.</p>
                        <p>Would you like to register and log in directly?</p>

                        <div className="modal-name-input">
                            <label htmlFor="modal-name">Name (Optional)</label>
                            <input
                                type="text"
                                id="modal-name"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="Your name"
                                maxLength={200}
                            />
                        </div>

                        <div className="modal-actions">
                            <button
                                className="auth-button"
                                onClick={handleRegisterConfirm}
                                disabled={loading}
                            >
                                {loading ? 'Registering...' : 'Register & Login'}
                            </button>
                            <button
                                className="modal-cancel-button"
                                onClick={handleRegisterCancel}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default UserAuth;
